/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DataAxisProps,
  Group,
  GroupAxisProps,
  TimeAxisProps,
  AxisPosition,
  ChartSeriesData,
  Scale
} from '../UNSAFE_visTypes/chart';
import { Dimension } from '../UNSAFE_visTypes/common';
import { BarItemData, BarSeriesData } from '../../UNSAFE_BarChart/barChart.types';
import {
  findNearestRegularAxisIndex,
  getDataExtent,
  getNearestMixedFrequencyDataItem
} from './dataUtils';
import { getAxesPosition } from '../../PRIVATE_Axis/utils/axisUtils';
import type { GetTextDimensionsType } from '../../hooks/PRIVATE_useTextDimensions';
import { cloneDimension } from '../../utils/PRIVATE_visLayoutUtils';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { ChartOverviewConfig } from '#utils/UNSAFE_visTypes/chart/chart.types';
import {
  getOverviewHeight,
  OVERVIEW_PANE_SIZE,
  TIME_AXIS_OVERVIEW_PANE_SIZE
} from './overviewUtils';
import { DataAxisInfo } from '#PRIVATE_Axis/dataAxis/dataAxisInfo';
import { getDataAxisProps } from '#PRIVATE_Axis/dataAxis/dataAxisUtils';
import {
  getStartAndEndTime,
  getTime,
  getTimeAxisInfo,
  getTimeAxisProps
} from '#PRIVATE_Axis/timeAxis/timeAxisInfo';
import { getGroupAxisInfo, getGroupAxisProps } from '#PRIVATE_Axis/groupAxis/groupAxisInfo';
import { generateLevels } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
const OUTER_GAP_WIDTH = 10;
const OUTER_GAP_HEIGHT = 8;
const MIN_SCROLL_SIZE = 12;
const SCROLL_GAP = 6;

/**
 * Returns the chart layout info.
 * @returns
 */
export function getLayoutInfo<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined,
  xAxis: GroupAxisProps | TimeAxisProps | DataAxisProps | undefined,
  yAxis: DataAxisProps | undefined,
  y2Axis: DataAxisProps | undefined,
  availSpace: Dimension,
  hiddenSet: Set<K>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  isHoriz: boolean,
  isRtl: boolean,
  isStacked: boolean,
  axisFontSize: string,
  getStartAndEndAxesOffset: (groupCount: number) => { startOffset: number; endOffset: number },
  locale: string,
  splitDualY: boolean,
  splitterPositin: number,
  getStackGroup?: (series: ChartSeriesData<K, D>) => string | undefined,
  getTextDimensions?: GetTextDimensionsType,
  isTestEnv?: boolean,
  hasXDataAxis?: boolean,
  initialZooming?: 'first' | 'last' | 'none',
  zoomAndScroll?: 'live' | 'off'
) {
  const isTimeAxis = (xAxis as TimeAxisProps)?.timeAxisType != null;
  const { xAxisPosition, yAxisPosition, y2AxisPosition } = getAxesPosition(
    yAxis,
    y2Axis,
    isHoriz,
    isRtl
  );
  let xAxisInfo;
  let dataInfo;

  const hasY2Axis = series.some((s) => s.associatedYAxis === 'y2');

  addAxisGaps(
    availSpace,
    isHoriz,
    isRtl,
    hasY2Axis,
    axisFontSize,
    yAxis,
    y2Axis,
    xAxis,
    getTextDimensions
  );

  const { levelsArray, getGroupFromPath, groupCount } = generateLevels(groups);
  const leafGroups =
    levelsArray.length > 0 ? levelsArray[levelsArray.length - 1].map((i) => i.item) : [];

  const { startOffset, endOffset } = getStartAndEndAxesOffset(groupCount);

  const getDataItemWithGroupPath = (seriesIndex: number, groupIndex: number) => {
    return getDataItem(
      seriesIndex,
      groupIndex,
      levelsArray[levelsArray.length - 1]?.[groupIndex]?.path
    );
  };

  if (hasXDataAxis) {
    dataInfo = getDataExtent<K, D>(
      series,
      leafGroups,
      getDataItemWithGroupPath,
      0,
      leafGroups.length - 1,
      isStacked,
      hiddenSet,
      hideAndShowBehavior,
      xAxis as DataAxisProps,
      undefined,
      getStackGroup
    );
    xAxisInfo = new DataAxisInfo({
      ...(xAxis as DataAxisProps),
      dataMin: dataInfo.xMin,
      dataMax: dataInfo.xMax,
      numGroups: leafGroups.length
    }).getInfo();
  } else {
    if (isTimeAxis) {
      const timeAxisRange = getStartAndEndTime(
        leafGroups,
        series,
        getDataItemWithGroupPath,
        xAxis as TimeAxisProps
      );
      xAxisInfo = getTimeAxisInfo(
        leafGroups,
        xAxis as TimeAxisProps,
        startOffset,
        endOffset,
        timeAxisRange,
        isHoriz,
        availSpace,
        zoomAndScroll,
        initialZooming,
        getTextDimensions
      );
    } else {
      xAxisInfo = getGroupAxisInfo(
        leafGroups,
        xAxis as GroupAxisProps,
        startOffset,
        endOffset,
        initialZooming,
        getTextDimensions,
        availSpace,
        isHoriz,
        zoomAndScroll
      );
    }

    dataInfo = getDataExtent<K, D>(
      series,
      leafGroups,
      getDataItemWithGroupPath,
      xAxisInfo.startIndex,
      xAxisInfo.endIndex,
      isStacked,
      hiddenSet,
      hideAndShowBehavior,
      yAxis,
      y2Axis,
      getStackGroup
    );
  }
  const {
    yDataMax,
    yDataMin,
    y2DataMax,
    y2DataMin,
    yMin,
    yMax,
    y2Min,
    y2Max,
    idToDataMap,
    isY2AxisRendered,
    isYAxisRendered,
    hasWidthWeightValue,
    barWidths
  } = dataInfo;

  let yAxisInfo, y2AxisInfo;
  if (y2DataMin < yDataMin || y2DataMax > yDataMax) {
    y2AxisInfo = new DataAxisInfo({
      ...y2Axis,
      dataMax: hasXDataAxis ? y2Max : y2DataMax,
      dataMin: hasXDataAxis ? y2Min : y2DataMin,
      numGroups: leafGroups.length
    }).getInfo();

    yAxisInfo = new DataAxisInfo({
      ...yAxis,
      dataMax: hasXDataAxis ? yMax : yDataMax,
      dataMin: hasXDataAxis ? yMin : yDataMin,
      majorTickCount: y2AxisInfo.ticks.length - 1,
      numGroups: leafGroups.length
    }).getInfo();
  } else {
    yAxisInfo = new DataAxisInfo({
      ...yAxis,
      dataMax: hasXDataAxis ? yMax : yDataMax,
      dataMin: hasXDataAxis ? yMin : yDataMin,
      numGroups: leafGroups.length
    }).getInfo();
    y2AxisInfo = new DataAxisInfo({
      ...y2Axis,
      dataMax: hasXDataAxis ? y2Max : y2DataMax,
      dataMin: hasXDataAxis ? y2Min : y2DataMin,
      majorTickCount: yAxisInfo.ticks.length - 1,
      numGroups: leafGroups.length
    }).getInfo();
  }
  const { axisDim: yDim } = getDataAxisProps(
    yAxisPosition,
    cloneDimension(availSpace),
    isRtl,
    locale,
    yAxisInfo,
    yAxis,
    axisFontSize,
    getTextDimensions,
    isYAxisRendered
  );

  const getYPreferredSize = (width: number, height: number) => {
    const availableSpace = getAvailSpace(width, height);
    const { axisDim: yDim } = getDataAxisProps(
      yAxisPosition,
      { width: availableSpace.width, height: availableSpace.height, x: 0, y: 0 },
      isRtl,
      locale,
      yAxisInfo,
      yAxis,
      axisFontSize,
      getTextDimensions,
      isYAxisRendered
    );
    return { width: isHoriz ? width : Math.ceil(yDim), height: isHoriz ? Math.ceil(yDim) : height };
  };

  const { axisDim: y2Dim } = getDataAxisProps(
    y2AxisPosition,
    cloneDimension(availSpace),
    isRtl,
    locale,
    y2AxisInfo,
    y2Axis,
    axisFontSize,
    getTextDimensions,
    isY2AxisRendered
  );
  const getY2PreferredSize = (width: number, height: number) => {
    const availableSpace = getAvailSpace(width, height);
    const { axisDim: y2Dim } = getDataAxisProps(
      y2AxisPosition,
      { width: availableSpace.width, height: availableSpace.height, x: 0, y: 0 },
      isRtl,
      locale,
      y2AxisInfo,
      y2Axis,
      axisFontSize,
      getTextDimensions,
      isY2AxisRendered
    );
    return {
      width: isHoriz ? width : Math.ceil(y2Dim),
      height: isHoriz ? Math.ceil(y2Dim) : height
    };
  };

  const isXAxisHoriz = xAxisPosition === 'bottom';

  // get updated available space for x axis.
  const { xSpace: xAvailSpace } = getSpaces(
    availSpace,
    xAxisPosition,
    yAxisPosition,
    y2AxisPosition,
    isXAxisHoriz ? availSpace.height : availSpace.width,
    yDim,
    y2Dim
  );

  const axisProps = isTimeAxis
    ? getTimeAxisProps(
        leafGroups,
        xAxisPosition,
        xAvailSpace,
        xAxisInfo.viewportMin!,
        xAxisInfo.viewportMax!,
        xAxisInfo.step,
        isRtl,
        xAxis as TimeAxisProps,
        axisFontSize,
        getTextDimensions,
        isTimeAxis
      )
    : hasXDataAxis
    ? getDataAxisProps(
        xAxisPosition,
        xAvailSpace,
        isRtl,
        locale,
        xAxisInfo,
        xAxis as DataAxisProps,
        axisFontSize,
        getTextDimensions
      )
    : getGroupAxisProps(
        xAxisPosition,
        xAvailSpace,
        isRtl,
        xAxisInfo.viewportMin,
        xAxisInfo.viewportMax,
        xAxisInfo.min,
        xAxisInfo.max,
        xAxisInfo.startIndex!,
        xAxisInfo.endIndex!,
        isHoriz,
        levelsArray,
        endOffset,
        barWidths,
        isStacked,
        xAxis as GroupAxisProps,
        axisFontSize,
        getTextDimensions,
        isTestEnv,
        groupCount,
        hasWidthWeightValue
      );

  const getXPreferredSize = (width: number, height: number) => {
    const availableSpace = getAvailSpace(width, height);
    const axisProps = isTimeAxis
      ? getTimeAxisProps(
          leafGroups,
          xAxisPosition,
          { width: availableSpace.width, height: availableSpace.height, x: 0, y: 0 },
          xAxisInfo.viewportMin!,
          xAxisInfo.viewportMax!,
          xAxisInfo.step,
          isRtl,
          xAxis as TimeAxisProps,
          axisFontSize,
          getTextDimensions,
          isTimeAxis
        )
      : hasXDataAxis
      ? getDataAxisProps(
          xAxisPosition,
          xAvailSpace,
          isRtl,
          locale,
          xAxisInfo,
          xAxis as DataAxisProps,
          axisFontSize,
          getTextDimensions
        )
      : getGroupAxisProps(
          xAxisPosition,
          xAvailSpace,
          isRtl,
          xAxisInfo.viewportMin,
          xAxisInfo.viewportMax,
          xAxisInfo.min,
          xAxisInfo.max,
          xAxisInfo.startIndex!,
          xAxisInfo.endIndex!,
          isHoriz,
          levelsArray,
          endOffset,
          barWidths,
          isStacked,
          xAxis as GroupAxisProps,
          axisFontSize,
          getTextDimensions,
          isTestEnv,
          groupCount,
          hasWidthWeightValue
        );
    return {
      width: isHoriz ? height : axisProps.axisDim + 2,
      height: isHoriz ? axisProps.axisDim + 2 : width
    };
  };

  const { axisDim: xMaxDim, scale: xScale, ...xProps } = axisProps;

  // recalculate y axis props after the x axis.
  const { ySpace: newYSpace } = getSpaces(
    availSpace,
    xAxisPosition,
    yAxisPosition,
    y2AxisPosition,
    xMaxDim!,
    isXAxisHoriz ? availSpace.width : availSpace.height,
    y2Dim
  );

  const { ySpace: newySpace, y2Space: newY2Space } = getYY2(
    splitDualY,
    splitterPositin,
    newYSpace,
    isHoriz,
    isRtl
  );

  const newYProps = getDataAxisProps(
    yAxisPosition,
    newySpace,
    isRtl,
    locale,
    yAxisInfo,
    yAxis,
    axisFontSize,
    getTextDimensions,
    isYAxisRendered
  );
  const newY2Props = getDataAxisProps(
    y2AxisPosition,
    newY2Space,
    isRtl,
    locale,
    y2AxisInfo,
    y2Axis,
    axisFontSize,
    getTextDimensions,
    isY2AxisRendered
  );

  const { axisDim: yMaxDim, scale: yScale, ...yProps } = newYProps;
  const { axisDim: y2MaxDim, scale: y2Scale, ...y2Props } = newY2Props;

  const { plotAreaSpace, xSpace, ySpace, y2Space } = getSpaces(
    availSpace,
    xAxisPosition,
    yAxisPosition,
    y2AxisPosition,
    xMaxDim!,
    yMaxDim,
    y2MaxDim
  );

  return {
    getGroupFromPath,
    leafGroups,
    levelsArray,
    xAxisInfo,
    xProps,
    xScale,
    xAxisPosition,
    yProps,
    y2Props,
    yScale,
    y2Scale,
    yMaxDim,
    y2MaxDim,
    yAxisPosition,
    y2AxisPosition,
    y2AxisInfo,
    yAxisInfo,
    plotAreaSpace,
    xSpace,
    ySpace,
    y2Space,
    idToDataMap,
    getYPreferredSize,
    getY2PreferredSize,
    getXPreferredSize
  };
}

/**
 * Returns the available space for x axis and y axis.
 * @param availSpace The available space of the chart.
 * @param xAxisPosition The position of x axis.
 * @param yAxisPosition The position of y axis.
 * @param xMaxDim The max dimension of x axis.
 * @param yMaxDim The max dimension of y axis.
 * @returns
 */
export function getSpaces(
  availSpace: Dimension,
  xAxisPosition: AxisPosition,
  yAxisPosition: AxisPosition,
  y2AxisPosition: AxisPosition,
  xMaxDim: number,
  yMaxDim: number,
  y2MaxDim: number
) {
  const isXHoriz = xAxisPosition === 'bottom' || xAxisPosition === 'top';
  const isYLeft = yAxisPosition === 'left';
  const isY2Left = y2AxisPosition === 'left';

  const isYBottom = yAxisPosition === 'bottom';
  const isY2Bottom = y2AxisPosition === 'bottom';

  let xSpace: Dimension;
  let ySpace: Dimension;
  let y2Space: Dimension;
  let plotAreaSpace: Dimension;

  if (isXHoriz) {
    xSpace = getHorizSpace(
      availSpace,
      xAxisPosition,
      isYLeft ? yMaxDim : isY2Left ? y2MaxDim : 0,
      isY2Left && !isYLeft ? yMaxDim : isYLeft && !isY2Left ? y2MaxDim : 0,
      xMaxDim
    );
    ySpace = getVertSpace(
      availSpace,
      yAxisPosition as 'left' | 'right',
      xAxisPosition === 'bottom' ? xMaxDim : 0,
      xAxisPosition === 'top' ? xMaxDim : 0,
      yMaxDim
    );
    y2Space = getVertSpace(
      availSpace,
      y2AxisPosition as 'left' | 'right',
      xAxisPosition === 'bottom' ? xMaxDim : 0,
      xAxisPosition === 'top' ? xMaxDim : 0,
      y2MaxDim
    );
    plotAreaSpace = {
      x: xSpace.x,
      y: ySpace.y,
      width: xSpace.width,
      height: ySpace.height
    };
  } else {
    xSpace = getVertSpace(
      availSpace,
      xAxisPosition,
      isYBottom ? yMaxDim : isY2Bottom ? y2MaxDim : 0,
      isY2Bottom && !isYBottom ? yMaxDim : isYBottom && !isY2Bottom ? y2MaxDim : 0,
      xMaxDim
    );
    ySpace = getHorizSpace(
      availSpace,
      yAxisPosition as 'top' | 'bottom',
      xAxisPosition === 'left' ? xMaxDim : 0,
      xAxisPosition === 'left' ? 0 : xMaxDim,
      yMaxDim
    );
    y2Space = getHorizSpace(
      availSpace,
      y2AxisPosition as 'top' | 'bottom',
      xAxisPosition === 'left' ? xMaxDim : 0,
      xAxisPosition === 'left' ? 0 : xMaxDim,
      y2MaxDim
    );

    plotAreaSpace = {
      x: ySpace.x,
      y: xSpace.y,
      width: ySpace.width,
      height: xSpace.height
    };
  }

  return { xSpace, ySpace, y2Space, plotAreaSpace };
}

/**
 * Returns the vertical sub section of given available space.
 * @param availSpace The available space.
 * @param position The position of the vertical section.
 * @param bottomOffset The bottom offset of the vertical section.
 * @param topOffset The top offset of the vertical section.
 * @param width The width of the section.
 * @returns
 */
export function getVertSpace(
  availSpace: Dimension,
  position: 'left' | 'right',
  bottomOffset: number,
  topOffset: number,
  width: number
) {
  return {
    x: position === 'left' ? availSpace.x : availSpace.x + availSpace.width - width,
    width: width,
    y: availSpace.y + topOffset,
    height: availSpace.height - bottomOffset - topOffset
  };
}

/**
 * Returns the horizontal sub section of given available space.
 * @param availSpace The available space.
 * @param position The position of the horizontal section.
 * @param leftOffset The left offset of the horizontal section.
 * @param rightOffset The right offset of the horizontal section.
 * @param height The height of the section.
 * @returns
 */
export function getHorizSpace(
  availSpace: Dimension,
  position: 'top' | 'bottom',
  leftOffset: number,
  rightOffset: number,
  height: number
) {
  return {
    x: availSpace.x + leftOffset,
    width: availSpace.width - leftOffset - rightOffset,
    y: position === 'top' ? availSpace.y : availSpace.y + availSpace.height - height,
    height: height
  };
}

/**
 * Returns the outer spacing for the chart.
 * @param width The height of the chart.
 * @param height The width of the chart.
 * @returns
 */
export function getOuterGaps(width: number, height: number) {
  const widthRatio = Math.min(width / 400, 1);
  const heightRatio = Math.min(height / 300, 1);
  return {
    width: widthRatio * OUTER_GAP_WIDTH,
    height: heightRatio * OUTER_GAP_HEIGHT
  };
}

/**
 * Returns the default available space for chart.
 * @param width The width of the chart.
 * @param height The height of the chart.
 * @returns
 */
export function getAvailSpace(width: number, height: number, needsOuterGap = true) {
  const gaps = getOuterGaps(width, height);
  if (!needsOuterGap) {
    gaps.width = Math.min(1, gaps.width);
    gaps.height = Math.min(1, gaps.height);
  }
  return {
    width: width - 2 * gaps.width,
    height: height - 2 * gaps.height,
    x: gaps.width,
    y: gaps.height
  };
}

/**
 * Retuns the available space for the scrollbar.
 * @param availSpace
 * @param zoomAndScroll
 * @param xAxisPosition
 * @returns
 */
export function getScrollBarSpace(
  isTimeAxis: boolean,
  availSpace: Dimension,
  zoomAndScroll: 'live' | 'off',
  xAxisPosition: AxisPosition,
  overview?: 'on' | 'off' | ChartOverviewConfig
) {
  const dims = { x: 0, y: 0, height: 0, width: 0 };
  const noOverviewOrScroll = zoomAndScroll === 'off' && overview === 'off';
  // for preact can still show overview without zoomAndScroll
  // small scroll bar present if overview is off, but zoomAndScroll is on.
  if (noOverviewOrScroll) {
    return { scrollDims: dims, availSpace };
  }
  const avSpace = cloneDimension(availSpace);

  let scrollSize = MIN_SCROLL_SIZE;
  const overviewSize = isTimeAxis ? TIME_AXIS_OVERVIEW_PANE_SIZE : OVERVIEW_PANE_SIZE;

  if (overview === 'on' && xAxisPosition === 'bottom') {
    scrollSize = availSpace.height * overviewSize;
  } else if (overview !== undefined) {
    scrollSize = getOverviewHeight(isTimeAxis, availSpace.height, overview);
  }

  if (xAxisPosition === 'left') {
    dims.x = availSpace.x;
    dims.y = availSpace.y;
    dims.width = scrollSize;
    dims.height = availSpace.height;

    avSpace.x += dims.width + SCROLL_GAP;
    avSpace.width -= dims.width + SCROLL_GAP;
  } else if (xAxisPosition === 'right') {
    dims.x = availSpace.x + availSpace.width - scrollSize;
    dims.width = scrollSize;
    dims.height = availSpace.height;
    dims.y = availSpace.y;

    avSpace.width -= dims.width + SCROLL_GAP;
  } else {
    dims.x = availSpace.x;
    dims.y = availSpace.y + availSpace.height - scrollSize;
    dims.height = scrollSize;
    dims.width = availSpace.width;

    avSpace.height -= dims.height + SCROLL_GAP;
  }
  return { scrollDims: dims, availSpace: avSpace };
}

/**
 * Updates the scrollbar space
 * @param availSpace
 * @param isHoriz
 * @param yMaxDim
 */
export function updateScrollBarSpace(
  availSpace: Dimension,
  isHoriz: boolean,
  isRtl: boolean,
  yMaxDim: number
) {
  const newDims = cloneDimension(availSpace);
  if (isHoriz) {
    newDims.width -= yMaxDim;
    newDims.x += isRtl ? 0 : yMaxDim;
  } else {
    newDims.height -= yMaxDim;
  }
  return newDims;
}

export function findNearestDataPoint<
  K extends string | number,
  D extends { x?: number | string; y?: number; value?: number; id: K }
>(
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  xScale: Scale,
  yScale: Scale,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  isStacked: boolean,
  getSeriesDataInfo: (
    seriesIndex: number,
    groupIndex: number
  ) => { offset: number; dataWidth: number },
  getYCoord: (
    value: number,
    yScale: Scale,
    isStacked: boolean,
    isLog: boolean,
    dataItemGaps: number,
    negCummValue: number,
    posCummValue: number
  ) => number,
  isLog: boolean,
  dataItemGaps: number,
  hiddenIds?: Set<number | string>,
  timeAxisType?: 'enabled' | 'mixedFrequency' | 'skipGaps',
  y2Scale?: Scale
) {
  return (x: number, y?: number, y2?: number) => {
    let candidates: { seriesIndex: number; groupIndex: number }[] = [];
    let neareastGroupIndex = Math.round(x);
    let groupCenter = xScale.transform(neareastGroupIndex);
    const isMixedFrequency = timeAxisType === 'mixedFrequency';

    if (timeAxisType === 'enabled' || timeAxisType === 'skipGaps') {
      neareastGroupIndex = findNearestRegularAxisIndex<K, D>(x, groups, series, getDataItem);
      groupCenter = xScale.transform(getTime(groups[neareastGroupIndex].id));
    }
    candidates = series.map((_, sIndex) => ({
      groupIndex: neareastGroupIndex,
      seriesIndex: sIndex
    }));

    if (isMixedFrequency) {
      candidates = getNearestMixedFrequencyDataItem<K, D>(x, series, groups, getDataItem);
    }

    let yPosCummValue = 0;
    let yNegCummValue = 0;

    let y2PosCummValue = 0;
    let y2NegCummValue = 0;

    let minDistance = Number.MAX_VALUE;
    let minDistanceDims2 = Number.MAX_VALUE;
    let minSeriesIndex = 0;
    let minSeriesCenter = groupCenter;

    let yCoord;

    // Currently this logic is aware of various chart settings. Another approach is
    // purely geometric (save dims of each chart item and sort by some distance measure).
    // Not sure which one is faster but can consider for performance evaluation.
    candidates.forEach(({ seriesIndex, groupIndex }) => {
      const isAssociatedToY2 = series[seriesIndex].associatedYAxis === 'y2';
      const item = getDataItem(seriesIndex, groupIndex);
      if (!item || hiddenIds?.has(item.id!) || item.value == null || (isLog && item.value <= 0)) {
        return;
      }

      let dist = 0;
      let dist2 = 0;

      if (isMixedFrequency) {
        dist = Math.abs(xScale.transform(getTime(item?.x!)) - xScale.transform(x));
        dist2 = isAssociatedToY2
          ? Math.abs(y2Scale!.transform(item.value) - y2Scale!.transform(y2!))
          : Math.abs(yScale.transform(item.value) - yScale.transform(y!));
      } else if (!isStacked) {
        const { offset, dataWidth } = getSeriesDataInfo(seriesIndex, groupIndex);
        const seriesCenter = offset + groupCenter + dataWidth / 2;
        if (dataWidth === 0) {
          dist = isAssociatedToY2
            ? Math.abs(y2Scale!.transform(item.value) - y2Scale!.transform(y2!))
            : Math.abs(yScale.transform(item.value) - yScale.transform(y!));
        } else {
          dist = Math.abs(xScale.transform(x) - seriesCenter);
          if (dist < minDistance) {
            minSeriesCenter = seriesCenter;
          }
        }
      } else {
        const itemY = isAssociatedToY2
          ? (item.value < 0 ? y2NegCummValue : y2PosCummValue) + item.value / 2
          : (item.value < 0 ? yNegCummValue : yPosCummValue) + item.value / 2;
        dist = isAssociatedToY2
          ? Math.abs(y2Scale!.transform(y2!) - y2Scale!.transform(itemY))
          : Math.abs(yScale.transform(y!) - yScale.transform(itemY));
      }

      if (dist < minDistance) {
        minDistance = dist;
        minSeriesIndex = seriesIndex;
        neareastGroupIndex = groupIndex;
        yCoord = getYCoord(
          item.value,
          isAssociatedToY2 ? y2Scale! : yScale,
          isStacked,
          isLog,
          dataItemGaps,
          isAssociatedToY2 ? y2NegCummValue : yNegCummValue,
          isAssociatedToY2 ? y2PosCummValue : yPosCummValue
        );
        if (isMixedFrequency) {
          minDistanceDims2 = dist2;
        }
      } else if (dist === minDistance) {
        if (isMixedFrequency && dist2 < minDistanceDims2) {
          minDistance = dist;
          minSeriesIndex = seriesIndex;
          neareastGroupIndex = groupIndex;
          minDistanceDims2 = dist2;
          yCoord = getYCoord(
            item.value,
            isAssociatedToY2 ? y2Scale! : yScale,
            isStacked,
            isLog,
            dataItemGaps,
            isAssociatedToY2 ? y2NegCummValue : yNegCummValue,
            isAssociatedToY2 ? y2PosCummValue : yPosCummValue
          );
        }
      }

      if (isStacked) {
        if (isAssociatedToY2) {
          y2PosCummValue += item.value > 0 ? item.value : 0;
          y2NegCummValue += item.value < 0 ? item.value : 0;
        } else {
          yPosCummValue += item.value > 0 ? item.value : 0;
          yNegCummValue += item.value < 0 ? item.value : 0;
        }
      }
    });

    const neareastItemId = getDataItem(minSeriesIndex, neareastGroupIndex)?.id;

    // the initial guess can also be hidden.
    if (neareastItemId == null || hiddenIds?.has(neareastItemId)) {
      return;
    }

    const isSeriesAssociatedToY2 = series[minSeriesIndex].associatedYAxis === 'y2';
    return {
      seriesIndex: minSeriesIndex,
      groupIndex: neareastGroupIndex,
      y: isSeriesAssociatedToY2 ? undefined : yCoord,
      y2: isSeriesAssociatedToY2 ? yCoord : undefined,
      x: isMixedFrequency
        ? xScale.transform(getTime(getDataItem(minSeriesIndex, neareastGroupIndex)?.x!))
        : isStacked
        ? groupCenter
        : minSeriesCenter
    };
  };
}

export function getGroupCenterCoord<
  K extends string | number,
  D extends { x?: number | string; y?: number; value?: number; id: K }
>(
  group: Group,
  groupIndex: number,
  xScale: Scale,
  item: D,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency'
) {
  if (!timeAxisType) {
    return xScale.transform(groupIndex);
  } else if (timeAxisType === 'mixedFrequency') {
    return xScale.transform(new Date(item.x!).getTime());
  } else {
    return xScale.transform(new Date(group.id).getTime());
  }
}

export function getMixedFreqGroupCenter<
  K extends string | number,
  D extends { x?: string; y?: number; value?: number; id: K }
>(xScale: Scale, item: D) {
  return xScale.transform(new Date(item.x!).getTime());
}

function getYY2(
  splitDualY: boolean,
  splitPosition: number,
  availSpace: Dimension,
  isHoriz: boolean,
  isRtl: boolean
) {
  if (!splitDualY) {
    return {
      ySpace: availSpace,
      y2Space: availSpace
    };
  }

  const { x, y, width, height } = availSpace;

  const y2Width = isHoriz ? width * splitPosition : width;
  const y2Height = isHoriz ? height : splitPosition * height;
  const yWidth = isHoriz ? width - y2Width : width;
  const yHeight = isHoriz ? height : height - y2Height;

  const y2x = isHoriz ? (isRtl ? x : x + yWidth) : x;
  const y2y = isHoriz ? y : y + yHeight;

  const yx = isHoriz ? (isRtl ? x + y2Width : x) : x;
  const yy = y;

  return {
    ySpace: {
      x: yx,
      y: yy,
      width: yWidth,
      height: yHeight
    },
    y2Space: {
      x: y2x,
      y: y2y,
      width: y2Width,
      height: y2Height
    }
  };
}

export function getYY2Ticks(
  yTicks: number[],
  y2Ticks: number[],
  isHoriz: boolean,
  isSplitDual: boolean
) {
  if (!isSplitDual) {
    return { yTicks, y2Ticks };
  }
  let _yTicks: number[] = [];
  let _y2Ticks: number[] = [];
  if (!isHoriz) {
    _yTicks = yTicks;
    _y2Ticks = y2Ticks.slice(0, -1);
  } else {
    _yTicks = yTicks.slice(0, -1);
    _y2Ticks = y2Ticks;
  }

  return { yTicks: _yTicks, y2Ticks: _y2Ticks };
}

export function addAxisGaps(
  availSpace: Dimension,
  isHoriz: boolean,
  isRtl: boolean,
  hasY2Axis: boolean,
  axisFontSize: string,
  yAxis?: DataAxisProps,
  y2Axis?: DataAxisProps,
  xAxis?: GroupAxisProps | TimeAxisProps | DataAxisProps,
  getTextDimensions?: GetTextDimensionsType
) {
  const { yAxisPosition, y2AxisPosition } = getAxesPosition(yAxis, y2Axis, isHoriz, isRtl);
  const isXRendered = !!(xAxis?.isRendered != false);
  const isYRendered = !!(yAxis?.isRendered != false);
  const isY2Rendered = hasY2Axis && !!(y2Axis?.isRendered != false);

  let axisGap = 6;

  const xAxisFontStyle = { fontSize: axisFontSize, ...xAxis?.tickLabel?.style };
  const yAxisFontStyle = { fontSize: axisFontSize, ...yAxis?.tickLabel?.style };
  const y2AxisFontStyle = { fontSize: axisFontSize, ...y2Axis?.tickLabel?.style };

  if (isHoriz) {
    axisGap *= (getTextDimensions?.('MW', xAxisFontStyle)?.height || 0) / 14; // adjust for font size
  } else {
    axisGap *=
      Math.max(
        getTextDimensions?.('MW', yAxisFontStyle)?.height || 0,
        getTextDimensions?.('MW', y2AxisFontStyle)?.height || 0
      ) / 14; // adjust for font size
  }
  axisGap = Math.ceil(axisGap); // prevent rounding errors

  // top gap if necessary
  if (
    (isHoriz &&
      !(yAxisPosition == 'top' && isYRendered) &&
      !(y2AxisPosition == 'top' && isY2Rendered)) ||
    !isHoriz
  ) {
    availSpace.y += axisGap;
    availSpace.height -= axisGap;
  }

  // bottom gap if necessary
  if (
    (isHoriz &&
      !(yAxisPosition == 'bottom' && isYRendered) &&
      !(y2AxisPosition == 'bottom' && isY2Rendered)) ||
    (!isHoriz && !isXRendered)
  ) {
    availSpace.height -= axisGap;
  }

  if (
    isHoriz &&
    (((yAxisPosition === 'bottom' || yAxisPosition === 'top') && isYRendered) ||
      ((y2AxisPosition === 'bottom' || y2AxisPosition === 'top') && isY2Rendered))
  ) {
    availSpace.width -= axisGap;
  }
}

/**
 * @param hasCenterdSeries Wheter the chart has centered series.
 * @param hasNonCenteredSeries Whether the chart has non centered series.
 * @param isHoriz Whether the chart is horizontal.
 * @param groupCount The group count of the chart.
 * @param hasBar Whether the chart contains bar series.
 * @returns
 */
export function getStartAndEndAxesOffset(
  hasCenterdSeries: boolean,
  hasUncenteredSeries: boolean,
  isHoriz: boolean,
  groupCount: number,
  hasBar?: boolean
) {
  // identical logic for offset the legacy chart impl for BLAC chart
  let offset = 0;
  if (hasBar || hasCenterdSeries) {
    offset = 0.5;
  } else {
    const maxOffset = isHoriz ? 0.2 : 0.5;
    offset = maxOffset - maxOffset / Math.sqrt(groupCount);
  }

  return hasUncenteredSeries
    ? { startOffset: -1 * offset, endOffset: offset + 1 }
    : { startOffset: -1 * offset, endOffset: offset };
}
